import React from 'react';
import { CatalystCountdownPanel } from '../components/panels/events/CatalystCountdownPanel';
import { LiveFeedPanel } from '../components/panels/events/LiveFeedPanel';
import { EconomicCalendarPanel } from '../components/panels/events/EconomicCalendarPanel';
import { ReformTrackerPanel } from '../components/panels/events/ReformTrackerPanel';
import { TimelinePanel } from '../components/panels/events/TimelinePanel';
import { RiskMonitorPanel } from '../components/panels/events/RiskMonitorPanel';
import { DealPipelinePanel } from '../components/panels/events/DealPipelinePanel';
import { usePanelManager, PanelConfig } from '../hooks/usePanelManager';
import { MinimizedBar } from '../components/ui/MinimizedBar';

// Panel configuration
const panelConfig: PanelConfig[] = [
  { id: 'catalyst', title: 'Catalyst Countdown', row: 1, colSpan: 2 },
  { id: 'feed', title: 'Live Feed', row: 1, colSpan: 2 },
  { id: 'calendar', title: 'Economic Calendar', row: 2, colSpan: 2 },
  { id: 'reform', title: 'Reform Tracker', row: 2, colSpan: 2 },
  { id: 'timeline', title: 'Timeline', row: 3, colSpan: 4 },
  { id: 'risk', title: 'Risk Monitor', row: 4, colSpan: 2 },
  { id: 'pipeline', title: 'Deal Pipeline', row: 4, colSpan: 2 },
];

// Panel component map
const panelComponents: Record<string, React.FC<{ isMinimized?: boolean; onMinimize?: () => void }>> = {
  catalyst: CatalystCountdownPanel,
  feed: LiveFeedPanel,
  calendar: EconomicCalendarPanel,
  reform: ReformTrackerPanel,
  timeline: TimelinePanel,
  risk: RiskMonitorPanel,
  pipeline: DealPipelinePanel,
};

const EventsView: React.FC = () => {
  const {
    minimizedPanelsList,
    handleMinimize,
    handleRestore,
    getOrderedPanels,
    isPanelMinimized,
    getDragState,
    getDragProps,
  } = usePanelManager(panelConfig);

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <MinimizedBar panels={minimizedPanelsList} onRestore={handleRestore} />

      <div className="flex-1 flex flex-col md:grid md:grid-cols-2 lg:grid-cols-4 md:grid-rows-[1fr_1fr_auto_1fr] gap-px p-px bg-terminal-border overflow-y-auto md:overflow-hidden">
        {/* Row 1 - Catalyst Countdown & Live Feed */}
        {getOrderedPanels(1).map(panel => (
          <div
            key={panel.id}
            {...getDragProps(panel.id)}
            className={`min-h-[200px] sm:min-h-[250px] md:min-h-0 col-span-1 lg:col-span-2 h-full transition-all duration-200 ${
              getDragState(panel.id).isDragging ? 'opacity-50 scale-[0.98]' : ''
            } ${getDragState(panel.id).isDragOver ? 'ring-2 ring-data-amber/50 ring-inset' : ''}`}
          >
            {React.createElement(panelComponents[panel.id], {
              isMinimized: isPanelMinimized(panel.id),
              onMinimize: () => handleMinimize(panel.id),
            })}
          </div>
        ))}

        {/* Row 2 - Economic Calendar & Reform Tracker */}
        {getOrderedPanels(2).map(panel => (
          <div
            key={panel.id}
            {...getDragProps(panel.id)}
            className={`min-h-[200px] sm:min-h-[250px] md:min-h-0 col-span-1 lg:col-span-2 h-full transition-all duration-200 ${
              getDragState(panel.id).isDragging ? 'opacity-50 scale-[0.98]' : ''
            } ${getDragState(panel.id).isDragOver ? 'ring-2 ring-data-amber/50 ring-inset' : ''}`}
          >
            {React.createElement(panelComponents[panel.id], {
              isMinimized: isPanelMinimized(panel.id),
              onMinimize: () => handleMinimize(panel.id),
            })}
          </div>
        ))}

        {/* Row 3 - Timeline */}
        {getOrderedPanels(3).map(panel => (
          <div
            key={panel.id}
            {...getDragProps(panel.id)}
            className={`min-h-[150px] sm:min-h-[180px] md:min-h-0 col-span-full h-full transition-all duration-200 ${
              getDragState(panel.id).isDragging ? 'opacity-50 scale-[0.98]' : ''
            } ${getDragState(panel.id).isDragOver ? 'ring-2 ring-data-amber/50 ring-inset' : ''}`}
          >
            {React.createElement(panelComponents[panel.id], {
              isMinimized: isPanelMinimized(panel.id),
              onMinimize: () => handleMinimize(panel.id),
            })}
          </div>
        ))}

        {/* Row 4 - Risk Monitor & Deal Pipeline */}
        {getOrderedPanels(4).map(panel => (
          <div
            key={panel.id}
            {...getDragProps(panel.id)}
            className={`min-h-[200px] sm:min-h-[250px] md:min-h-0 col-span-1 lg:col-span-2 h-full transition-all duration-200 ${
              getDragState(panel.id).isDragging ? 'opacity-50 scale-[0.98]' : ''
            } ${getDragState(panel.id).isDragOver ? 'ring-2 ring-data-amber/50 ring-inset' : ''}`}
          >
            {React.createElement(panelComponents[panel.id], {
              isMinimized: isPanelMinimized(panel.id),
              onMinimize: () => handleMinimize(panel.id),
            })}
          </div>
        ))}
      </div>
    </div>
  );
};

export default EventsView;
